import {
	Component,
	ElementRef,
	inject,
	Input,
	input,
	NgZone,
	OnDestroy,
	OnInit,
	output,
	viewChild,
	ViewContainerRef,
	ChangeDetectionStrategy,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { PluginRegistry } from "common/editor/description.service";
import { EditorConfigService, StagesEditorConfig } from "common/editor/editor-config.service";
import { NonProcessEditorProperties, PluginService } from "common/editor/plugin.service";
import { RepositionToolbarService, ToolbarPosition } from "common/editor/reposition-toolbar.service";
import { AnchorProcessor } from "common/editor/shared/anchor-processor";
import { StringEscapeUtilsBase } from "common/editor/shared/string-escape-utils";
import { assertDefined } from "core/functions";
import { MainService } from "core/main.service";
import { BehaviorSubject, Observable, ReplaySubject, Subject } from "rxjs";
import { debounceTime, defaultIfEmpty, distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { ViewService } from "core/view.service";
import { AsyncPipe } from "@angular/common";
import { AiFloatingMenuComponent } from "./ai-content/ai-floating-menu/ai-floating-menu.component";
import { DisclaimerMainComponent } from "common/disclaimer/main/disclaimer-main.component";
import { EditorToDisplayDescriptionMap } from "common/editor/editor-to-display-description-map";
import Prompt = stages.common.Prompt;
import { DescriptionScrollService } from "common/editor/description-scroll.service";
import { TranslateModule } from "@ngx-translate/core";

type DisplayDescription = stages.core.DisplayDescription;
type EditorConfig = stages.context.EditorConfig;

@Component({
	selector: "stages-description-editor",
	templateUrl: "./description-editor.component.html",
	styleUrls: ["./description-editor.component.scss"],
	providers: [
		{
			//https://indepth.dev/never-again-be-confused-when-implementing-controlvalueaccessor-in-angular-forms/
			provide: NG_VALUE_ACCESSOR,
			useExisting: DescriptionEditorComponent,
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AiFloatingMenuComponent, DisclaimerMainComponent, AsyncPipe, TranslateModule],
})
export class DescriptionEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
	private viewContainerRef = inject(ViewContainerRef);
	private viewService = inject(ViewService);
	private configService = inject(EditorConfigService);
	private repositionToolbarService = inject(RepositionToolbarService);
	private pluginService = inject(PluginService);
	private zone = inject(NgZone);
	private mainService = inject(MainService);
	private editorToDisplayDescriptionMap = inject(EditorToDisplayDescriptionMap);
	private descriptionScrollService = inject(DescriptionScrollService);

	editor?: CKEDITOR.editor;

	eventScrollListener!: EventListenerOrEventListenerObject;

	panelId!: string;
	toolbarId!: string;
	toolbarPosition: ToolbarPosition = "top";

	displayDescription!: stages.core.DisplayDescription;

	editorConfig!: EditorConfig;

	editorOptions!: StagesEditorConfig;

	editableDescriptionHtml!: string;

	editableDescriptionHtmlOriginal!: string;

	editedImageMap!: Record<string, string>;

	isTextCreatorConfigured$ = this.mainService.applicationContext$.pipe(
		map(
			(applicationContext) =>
				applicationContext.textCreatorConfigured && applicationContext.aiAcknowledgementsState !== "DECLINED",
		),
	);

	private selection$ = new ReplaySubject<CKEDITOR.dom.selection>(1);
	selectedHtml$: Observable<string>;
	protected prompts: Prompt[] = [];

	isEditorAvailable$ = new BehaviorSubject<boolean>(false);

	private destroy$ = new Subject<boolean>();

	readonly editorId = input.required<string>();

	readonly modifiable = input<boolean>();

	readonly unsafe = input.required<boolean>();

	readonly processTypeIdent = input<string | null>(null);

	readonly pluginRegistry = input.required<PluginRegistry>();

	readonly beanType = input.required<string>();

	readonly beanId = input.required<string>();

	readonly beanIdentity = input.required<string>();

	readonly workspaceId = input.required<string>();

	readonly pv = input.required<string>();

	readonly properties = input<NonProcessEditorProperties>();

	readonly focus = input<boolean>(true);

	readonly deactivateFloatingButton = input<boolean>(false);

	readonly editEnabledChange = output<boolean>();

	readonly editorElementRef = viewChild.required<ElementRef>("editordiv");

	/** IMPLEMENTS  ControlValueAccessor */
	onChange?: (value: stages.core.DisplayDescription) => void;

	onTouched?: () => void;

	writeValue(obj: stages.core.DisplayDescription): void {
		this.description = obj ?? { editedImageMap: {}, html: "", language: null, viewingMode: "EDIT" };
	}

	registerOnChange(fn: (value: stages.core.DisplayDescription) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		if (this.editor) {
			this.editor.setReadOnly(isDisabled);
		}
	}

	/** END OF IMPLEMENTS  ControlValueAccessor */

	onWindowScroll(): void {
		if (this.modifiable()) {
			this.toolbarPosition = this.repositionToolbarService.repositionToolbar(
				this.toolbarPosition,
				this.panelId,
				this.toolbarId,
			);
		}
	}

	constructor() {
		this.selectedHtml$ = this.selection$.pipe(
			debounceTime(500),
			map((selection) => this.getSelectionHtml(selection)),
			distinctUntilChanged(),
			defaultIfEmpty(""),
		);
	}

	ngOnInit(): void {
		assertDefined(this.beanId);
		assertDefined(this.beanIdentity);
		assertDefined(this.pv);
		this.panelId = this.editorId() + "-panel";
		this.toolbarId = this.editorId() + "-toolbar";
		this.eventScrollListener = () => this.onWindowScroll();

		if (!this.deactivateFloatingButton()) {
			this.viewService
				.awaitSelfElementObservable()
				.pipe(
					map((view) => {
						const promptContainer = view.promptContainer;
						if (promptContainer?.elementTypeIdent?.toLowerCase() === this.beanType().toLowerCase()) {
							const tempPrompts = promptContainer.prompts.filter((p) => p.promptType !== "NAMES");
							if (tempPrompts) {
								this.prompts = tempPrompts;
							}
						}
					}),
					takeUntil(this.destroy$),
				)
				.subscribe();
		}
		void this.reloadEditorConfig();
	}

	ngOnDestroy(): void {
		this.quit();
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input()
	set description(displayDescription: stages.core.DisplayDescription) {
		this.edit(displayDescription);
	}

	edit(description: stages.core.DisplayDescription): void {
		this.editedImageMap = description.editedImageMap && {};
		this.displayDescription = description;
		this.editableDescriptionHtml = description.html;
		this.editableDescriptionHtmlOriginal = description.html;
		if (this.editor) {
			this.editor.setData(this.editableDescriptionHtml);
			// called from process element view
			this.isEditorAvailable$.next(true);
		}
	}

	async reloadEditorConfig(): Promise<void> {
		this.editorConfig =
			this.editorToDisplayDescriptionMap.get(this.editorId(), this.beanType() ?? "process", this.beanId())
				?.editorConfig ?? (await this.configService.getEditorConfig(this.processTypeIdent(), this.workspaceId()));

		this.editorOptions = this.configService.getEditorOptions(
			this.editorConfig,
			"",
			"",
			this.unsafe(),
			this.toolbarId,
			this.focus() /* focus */,
			0,
			this.pluginRegistry(),
			!this.modifiable(),
		);

		const editorProperties = {};

		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
		(editorProperties as any)["editor_" + this.editorId()] = this.pluginService.Editor(
			this.viewContainerRef,
			this.zone,
			this.pluginRegistry(),
			this.workspaceId(),
			this.beanType(),
			this.beanId(),
			this.beanIdentity(),
			this.pv(),
			this.properties(),
		);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
		const pkit = (window as any).PKIT;
		pkit.ckeditor.editorProperties = { ...pkit.ckeditor.editorProperties, ...editorProperties };
		pkit.util = {};
		pkit.util.StringEscapeUtils = StringEscapeUtilsBase;
		pkit.editor = {};
		pkit.editor.AnchorProcessor = AnchorProcessor;
		pkit.tref = {};
		CKEDITOR.disableAutoInline = true;
		this.zone.runOutsideAngular(() => {
			if (CKEDITOR.instances[this.editorId()]) {
				CKEDITOR.instances[this.editorId()].destroy();
			}
			this.editor = CKEDITOR.inline(this.editorElementRef().nativeElement, this.editorOptions);
			this.editor.setData(this.editableDescriptionHtml);
			this.editor.on("instanceReady", (instanceReady) => {
				this.zone.run(() => {
					instanceReady.editor.document.on("selectionchange", (evt) => {
						this.selection$.next(evt.sender.getSelection());
					});
				});
			});
		});
		this.descriptionScrollService.addEventListener(this.editorElementRef(), this.eventScrollListener);
		this.editEnabledChange.emit(true);
		setTimeout(() => {
			if (this.modifiable()) {
				this.toolbarPosition = this.repositionToolbarService.repositionToolbar(
					this.toolbarPosition,
					this.panelId,
					this.toolbarId,
				);
			}
			if (this.editor) {
				// called from process element edit
				this.isEditorAvailable$.next(true);
				this.editor.on("change", () => {
					if (this.onChange) {
						const newData = this.editor!.getData();
						if (this.editableDescriptionHtml !== newData) {
							this.editableDescriptionHtml = newData;
							this.onChange(this.getDescription());
						}
					}
				});
				this.editor.on("blur", () => {
					if (this.onTouched) {
						this.onTouched();
					}
				});
			}
		}, 0);
	}

	quit(): void {
		if (this.eventScrollListener) {
			window.removeEventListener("scroll", this.eventScrollListener);
		}
		setTimeout(() => {
			if (this.editor) {
				this.editor.getSelection()?.reset();
				this.editor.removeAllListeners();
				this.editor.destroy();
			}
			this.editor = undefined;
		});
	}

	cancel(): void {
		this.quit();
	}

	getDescription(): stages.core.DisplayDescription {
		return {
			viewingMode: this.displayDescription.viewingMode,
			html: this.editor ? this.editor.getData() : this.editableDescriptionHtmlOriginal,
			language: this.displayDescription.language,
			editedImageMap: this.editedImageMap,
		};
	}

	getDescriptionAndEditorConfig(): { description: DisplayDescription; editorConfig: EditorConfig } {
		return {
			description: this.getDescription(),
			editorConfig: this.editorConfig,
		};
	}

	updateEditorContent(updatedText: string): void {
		if (this.editor) {
			this.editor?.insertHtml(updatedText);
		}
	}

	getSelectionHtml(selection: CKEDITOR.dom.selection | null): string {
		if (selection === null) {
			return "";
		}

		const ranges = selection.getRanges();
		const element = new CKEDITOR.dom.element("div");
		for (let i = 0, len = ranges.length; i < len; ++i) {
			element.append(ranges[i].cloneContents());
		}
		return element.getHtml();
	}
}
